// Painel Professor (/teacher/) — réplica + upgrade do teacher/index.html legado.
// 4 abas: Painel, Bancos, Turmas, Provas.
// Auth: contexto company com role teacher (admin de plataforma também passa).

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowLeftRight,
  BookOpen,
  Building2,
  Check,
  ChevronDown,
  ClipboardList,
  Database,
  GraduationCap,
  Home as HomeIcon,
  Loader2,
  LogOut,
  Menu,
  Moon,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  PencilLine,
  Sun,
  Users,
  X
} from "lucide-react";
import { getActiveTheme, toggleTheme } from "@app/ui/lib/theme";
import { bootstrapFeatureFlags } from "@app/ui/lib/feature-flags";
import { api, ApiError, type ActiveContext, type UserSession } from "./api";
import { PainelView } from "./views/PainelView";
import { BancosView } from "./views/BancosView";
import { DisciplinasView } from "./views/DisciplinasView";
import { QuestionEditorView } from "./views/QuestionEditorView";
import { TurmasView } from "./views/TurmasView";
import { ProvasView } from "./views/ProvasView";
import { ConfirmRoot, PromptRoot } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

type SectionKey =
  | "painel"
  | "disciplinas"
  | "bancos"
  | "editor"
  | "turmas"
  | "provas";

interface SectionMeta {
  key: SectionKey;
  label: string;
  icon: typeof Users;
  description: string;
  /** Sub-níveis opcionais. [[project-sidebar-submenus-backlog]] */
  children?: Array<{ key: SectionKey; label: string; icon?: typeof Users }>;
}

const SECTIONS: SectionMeta[] = [
  { key: "painel", label: "Painel", icon: HomeIcon, description: "Visão geral das suas atividades" },
  // BookOpen pra Disciplinas — consistência cross-portal (student-new e
  // company-new também usam BookOpen pra esse conceito).
  { key: "disciplinas", label: "Disciplinas", icon: BookOpen, description: "Suas disciplinas, conteúdos, pacotes e avaliações" },
  { key: "bancos", label: "Meus bancos", icon: Database, description: "Bancos de questões que você criou" },
  { key: "editor", label: "Editor de questões", icon: PencilLine, description: "Crie e edite questões dos seus bancos" },
  { key: "turmas", label: "Turmas", icon: GraduationCap, description: "Suas turmas — alunos e bancos liberados" },
  { key: "provas", label: "Provas agendadas", icon: ClipboardList, description: "Crie, monitore e revise provas" }
];

const SIDEBAR_KEY = "teacher-new-sidebar-collapsed";
function initialCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === "1";
  } catch {
    return false;
  }
}

const activeSection = signal<SectionKey>("painel");
const sidebarCollapsed = signal<boolean>(initialCollapsed());
const sidebarMobileOpen = signal<boolean>(false);
const themeSignal = signal<"light" | "dark">(getActiveTheme());

type Phase = "loading" | "ready" | "error" | "forbidden";
const phase = signal<Phase>("loading");
const user = signal<UserSession | null>(null);
const activeContext = signal<ActiveContext | null>(null);
const availableContexts = signal<ActiveContext[]>([]);
const errorMsg = signal<string | null>(null);

function isTeacherCtx(ctx: ActiveContext | null): boolean {
  return !!ctx && ctx.kind === "company" && (ctx.role === "teacher" || ctx.role === "admin");
}

function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value;
  try {
    localStorage.setItem(SIDEBAR_KEY, sidebarCollapsed.value ? "1" : "0");
  } catch {}
}

export function toggleSidebarResponsive() {
  if (typeof window === "undefined") return;
  if (window.matchMedia("(min-width: 768px)").matches) {
    toggleSidebar();
  } else {
    sidebarMobileOpen.value = !sidebarMobileOpen.value;
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("theme:changed", (e: any) => {
    themeSignal.value = e?.detail?.theme ?? getActiveTheme();
  });
}

async function bootstrap() {
  phase.value = "loading";
  try {
    const res = await api.me();
    user.value = res.user;
    activeContext.value = res.activeContext;
    availableContexts.value = res.contexts;
    if (!isTeacherCtx(res.activeContext)) {
      // Tenta encontrar um contexto teacher
      const teacherCtx = res.contexts.find(
        (c) => c.kind === "company" && c.role === "teacher"
      );
      if (teacherCtx) {
        await api.switchContext({
          kind: "company",
          role: "teacher",
          companyId: teacherCtx.companyId
        });
        const after = await api.me();
        user.value = after.user;
        activeContext.value = after.activeContext;
      } else {
        phase.value = "forbidden";
        return;
      }
    }
    void bootstrapFeatureFlags("teacher");
    phase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errorMsg.value = friendlyError(err);
    phase.value = "error";
  }
}

async function doLogout() {
  try {
    await api.logout();
  } catch {}
  window.location.href = "/auth/";
}

export function App() {
  useEffect(() => {
    bootstrap();
  }, []);

  if (phase.value === "loading") {
    return (
      <div class="grid min-h-screen place-items-center bg-background text-foreground">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="grid min-h-screen place-items-center bg-background text-foreground">
        <div class="max-w-md space-y-3 rounded-md border border-destructive/30 bg-destructive/5 p-6 text-sm">
          <h1 class="flex items-center gap-2 text-lg font-bold text-destructive">
            <AlertCircle className="size-5" />
            Falha ao carregar
          </h1>
          <p class="text-muted-foreground">{errorMsg.value}</p>
          <button
            type="button"
            onClick={bootstrap}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }
  if (phase.value === "forbidden") {
    return (
      <div class="grid min-h-screen place-items-center bg-background text-foreground">
        <div class="max-w-md space-y-3 rounded-md border border-border bg-card p-6">
          <h1 class="text-lg font-bold">Acesso restrito</h1>
          <p class="text-sm text-muted-foreground">
            Você não tem perfil de professor em nenhuma organização. Peça pro admin da
            sua escola pra te dar esse vínculo.
          </p>
          <button
            type="button"
            onClick={() => (window.location.href = "/auth/?picker=1")}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Trocar perfil
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <ReadyShell />
      <ConfirmRoot />
      <PromptRoot />
    </>
  );
}

function ReadyShell() {
  const collapsed = sidebarCollapsed.value;
  const mainOffset = collapsed ? "md:ml-16" : "md:ml-72";
  return (
    <div class="min-h-screen bg-background text-foreground">
      <Sidebar />
      <div class={`flex min-h-screen min-w-0 flex-col transition-[margin] duration-200 ${mainOffset}`}>
        <Topbar />
        <main class="flex-1 px-5 py-6 md:px-8 md:py-8">
          <SectionRouter />
        </main>
        <footer class="border-t border-border bg-card/30 px-5 py-3 text-center text-[11px] text-muted-foreground/70 md:px-8">
          dev · /teacher/ · Ctrl+/ ou Ctrl+\ alternam a sidebar
        </footer>
      </div>
    </div>
  );
}

function Topbar() {
  const section = SECTIONS.find((s) => s.key === activeSection.value);
  return (
    <div class="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-background/80 px-5 py-3 backdrop-blur-md md:px-8">
      <button
        type="button"
        class="rounded-md border border-border bg-background px-2 py-1 md:hidden"
        onClick={() => (sidebarMobileOpen.value = true)}
        title="Abrir menu"
        aria-label="Abrir menu"
      >
        <Menu width="1em" height="1em" />
      </button>
      <div class="min-w-0 flex-1">
        <h2 class="flex items-center gap-1.5 truncate text-sm font-semibold">
          {/* Root: nome da seção. Quando há subCrumbs, vira link clicável que
              volta pra raiz (limpando todos os crumbs). */}
          {subCrumbs.value.length > 0 ? (
            <button
              type="button"
              onClick={() => (subCrumbs.value = [])}
              class="truncate font-semibold text-muted-foreground transition-colors hover:text-foreground"
            >
              {section?.label ?? "—"}
            </button>
          ) : (
            <span class="truncate">{section?.label ?? "—"}</span>
          )}
          {subCrumbs.value.map((c, i) => (
            <span key={i} class="inline-flex min-w-0 items-center gap-1.5">
              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
              {c.onClick && i < subCrumbs.value.length - 1 ? (
                <button
                  type="button"
                  onClick={c.onClick}
                  class="truncate text-muted-foreground transition-colors hover:text-foreground"
                >
                  {c.label}
                </button>
              ) : (
                <span class="truncate text-foreground">{c.label}</span>
              )}
            </span>
          ))}
        </h2>
        {activeContext.value?.companyName && (
          <p class="truncate text-[11px] text-muted-foreground">
            {activeContext.value.companyName}
          </p>
        )}
      </div>
    </div>
  );
}

/** Quando seteado, o Editor de Questões abre direto neste banco. Ao limpar,
 *  o editor volta pra tela de "escolher banco". A BancosView seta isso ao
 *  clicar em "Editar questões" e troca a section pra "editor" — assim o
 *  fluxo é único: Bancos lista todos, Editor é o workspace de edição. */
export const editingBankId = signal<string | null>(null);

export function openBankInEditor(bankId: string) {
  editingBankId.value = bankId;
  activeSection.value = "editor";
}

/** Sub-crumbs renderizados depois do label da seção atual. Permitem que uma
 *  view sub-navegue (ex.: Disciplinas → "Bases Clínicas"; Editor → "Banco X")
 *  e o breadcrumb reflita o contexto. Cada item pode ter `onClick` pra voltar
 *  pra um nível anterior; sem `onClick`, vira folha (não clicável). */
export interface CrumbItem {
  label: string;
  onClick?: () => void;
}
export const subCrumbs = signal<CrumbItem[]>([]);

/** Quando seteada, a DisciplinasView abre direto neste id como view de
 *  detalhe (não modal). Limpar pra voltar pra lista. */
export const viewingDisciplinaId = signal<string | null>(null);

export function openDisciplinaView(id: string) {
  viewingDisciplinaId.value = id;
  activeSection.value = "disciplinas";
}

function SectionRouter() {
  const key = activeSection.value;
  if (key === "painel") return <PainelView />;
  if (key === "disciplinas") return <DisciplinasView />;
  if (key === "bancos") return <BancosView />;
  if (key === "editor") return <QuestionEditorView />;
  if (key === "turmas") return <TurmasView />;
  if (key === "provas") return <ProvasView />;
  return null;
}

function Sidebar() {
  const collapsed = sidebarCollapsed.value;
  const mobileOpen = sidebarMobileOpen.value;
  const widthClass = collapsed ? "md:w-16" : "md:w-72";
  const mobilePos = mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0";
  const sectionPadX = collapsed ? "px-2" : "px-4";

  return (
    <>
      {mobileOpen && (
        <div
          class="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => (sidebarMobileOpen.value = false)}
        />
      )}
      <aside
        class={`fixed inset-y-0 left-0 z-40 flex w-72 flex-col border-r border-border bg-card transition-all duration-200 ${widthClass} ${mobilePos}`}
      >
        <div
          class={`flex h-16 items-center border-b border-border ${sectionPadX} ${
            collapsed ? "justify-center" : "gap-2"
          }`}
        >
          <div class="grid size-9 shrink-0 place-items-center rounded-md bg-brand-gradient text-white shadow-sm ring-1 ring-white/10">
            <GraduationCap width="1.2em" height="1.2em" strokeWidth={2.25} />
          </div>
          {!collapsed && (
            <>
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-bold leading-tight">Painel Professor</div>
                <div class="truncate text-[11px] text-muted-foreground">
                  {user.value?.name ?? user.value?.email ?? "—"}
                </div>
              </div>
              <button
                type="button"
                onClick={toggleSidebar}
                title="Recolher sidebar (Ctrl+/)"
                aria-label="Recolher sidebar"
                class="hidden size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:grid"
              >
                <PanelLeftClose className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => (sidebarMobileOpen.value = false)}
                title="Fechar"
                aria-label="Fechar menu"
                class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:hidden"
              >
                <X className="size-4" />
              </button>
            </>
          )}
        </div>

        {collapsed && (
          <div class="hidden border-b border-border px-2 py-2 md:block">
            <button
              type="button"
              onClick={toggleSidebar}
              title="Expandir sidebar (Ctrl+/)"
              aria-label="Expandir sidebar"
              class="grid h-9 w-full place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <PanelLeftOpen className="size-4" />
            </button>
          </div>
        )}

        {!collapsed && <ContextSwitcher />}

        <nav class={`flex-1 space-y-1 py-5 ${sectionPadX}`}>
          {SECTIONS.map((section) => (
            <NavLink
              key={section.key}
              section={section}
              isActive={activeSection.value === section.key}
              collapsed={collapsed}
              onClick={() => {
                activeSection.value = section.key;
                sidebarMobileOpen.value = false;
              }}
              activeKey={activeSection.value}
              onChildClick={(childKey) => {
                activeSection.value = childKey;
                sidebarMobileOpen.value = false;
              }}
            />
          ))}
        </nav>

        <div class={`space-y-1 border-t border-border py-4 ${sectionPadX}`}>
          <button
            type="button"
            onClick={() => {
              const next = toggleTheme();
              themeSignal.value = next;
            }}
            title={collapsed ? (themeSignal.value === "dark" ? "Tema claro" : "Tema escuro") : undefined}
            class={`flex w-full items-center rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${
              collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
            }`}
          >
            {themeSignal.value === "dark" ? (
              <Sun width="1.3em" height="1.3em" />
            ) : (
              <Moon width="1.3em" height="1.3em" />
            )}
            {!collapsed && <span>Tema {themeSignal.value === "dark" ? "claro" : "escuro"}</span>}
          </button>
          <button
            type="button"
            onClick={() => {
              window.location.href = "/auth/?picker=1";
            }}
            title={collapsed ? "Trocar perfil" : undefined}
            class={`flex w-full items-center rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${
              collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
            }`}
          >
            <ArrowLeftRight width="1.3em" height="1.3em" />
            {!collapsed && <span>Trocar perfil</span>}
          </button>
          <button
            type="button"
            onClick={doLogout}
            title={collapsed ? "Sair" : undefined}
            class={`flex w-full items-center rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive ${
              collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
            }`}
          >
            <LogOut width="1.3em" height="1.3em" />
            {!collapsed && <span>Sair</span>}
          </button>
        </div>
      </aside>
    </>
  );
}

function ContextSwitcher() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const active = activeContext.value;
  // Só mostra contextos teacher (mesmo role do painel atual)
  const teacherCtxs = availableContexts.value.filter(
    (c) => c.kind === "company" && c.role === "teacher"
  );
  if (!active || teacherCtxs.length === 0) return null;
  const others = teacherCtxs.filter(
    (c) => c.companyId !== active.companyId
  );

  async function trocar(ctx: ActiveContext) {
    if (busy) return;
    if (ctx.companyId === active?.companyId) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      await api.switchContext({
        kind: "company",
        role: "teacher",
        companyId: ctx.companyId
      });
      window.location.reload();
    } catch (err) {
      console.error("switchContext", err);
      setBusy(false);
    }
  }

  const placeName = active.companyName ?? "—";
  const isMulti = teacherCtxs.length >= 2;

  return (
    <div class="border-b border-border px-3 py-2">
      <button
        type="button"
        onClick={() => isMulti && setOpen((o) => !o)}
        disabled={!isMulti || busy}
        title={isMulti ? "Trocar organização" : "Organização atual"}
        class={`flex w-full items-center gap-2 rounded-md border border-border bg-background px-2.5 py-2 text-left text-xs transition-colors ${
          isMulti ? "hover:bg-accent" : "opacity-80"
        }`}
      >
        <Building2 className="size-3.5 shrink-0 text-primary" />
        <div class="min-w-0 flex-1">
          <div class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Organização
          </div>
          <div class="truncate font-semibold">{placeName}</div>
        </div>
        {isMulti && (
          <ChevronDown
            className={`size-3.5 shrink-0 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        )}
      </button>
      {open && isMulti && (
        <ul class="mt-1 space-y-0.5 rounded-md border border-border bg-background p-1">
          {teacherCtxs.map((c) => {
            const isActive = c.companyId === active.companyId;
            return (
              <li key={c.companyId ?? ""}>
                <button
                  type="button"
                  onClick={() => trocar(c)}
                  disabled={busy}
                  class={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-foreground hover:bg-accent"
                  }`}
                >
                  <Building2 className="size-3.5 shrink-0" />
                  <span class="min-w-0 flex-1 truncate">
                    {c.companyName ?? "—"}
                  </span>
                  {busy && !isActive && (
                    <Loader2 className="size-3 shrink-0 animate-spin" />
                  )}
                  {isActive && <Check className="size-3.5 shrink-0" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {!isMulti && others.length === 0 && (
        <p class="mt-1 px-1 text-[10px] text-muted-foreground/70">
          {/* Single org: nada a fazer aqui */}
        </p>
      )}
    </div>
  );
}

function NavLink({
  section,
  isActive,
  collapsed,
  onClick,
  activeKey,
  onChildClick
}: {
  section: SectionMeta;
  isActive: boolean;
  collapsed: boolean;
  onClick: () => void;
  activeKey?: SectionKey;
  onChildClick?: (key: SectionKey) => void;
}) {
  const Icon = section.icon;
  const hasChildren = !!section.children && section.children.length > 0;
  const childActive = hasChildren && activeKey
    ? section.children!.some((c) => c.key === activeKey)
    : false;
  const [expanded, setExpanded] = useState<boolean>(() => {
    if (!hasChildren) return false;
    try {
      const raw = localStorage.getItem("sidebar.expanded.teacher");
      const arr = raw ? (JSON.parse(raw) as unknown) : null;
      if (Array.isArray(arr) && arr.includes(section.key)) return true;
    } catch { /* ignore */ }
    return childActive;
  });
  useEffect(() => {
    if (childActive && !expanded) setExpanded(true);
  }, [childActive]);
  function toggleExpanded() {
    const next = !expanded;
    setExpanded(next);
    try {
      const raw = localStorage.getItem("sidebar.expanded.teacher");
      const arr = raw ? (JSON.parse(raw) as unknown) : null;
      const set = new Set<string>(Array.isArray(arr) ? (arr as string[]) : []);
      if (next) set.add(section.key);
      else set.delete(section.key);
      localStorage.setItem("sidebar.expanded.teacher", JSON.stringify(Array.from(set)));
    } catch { /* ignore */ }
  }

  return (
    <>
      <button
        type="button"
        onClick={hasChildren ? toggleExpanded : onClick}
        title={collapsed ? section.label : undefined}
        {...(hasChildren ? { "aria-expanded": expanded ? "true" : "false" } : {})}
        class={`flex w-full items-center rounded-md text-sm font-medium transition-colors ${
          collapsed ? "justify-center px-2 py-2.5" : "gap-2.5 px-3 py-2 text-left"
        } ${
          isActive || childActive
            ? "bg-brand-gradient text-white shadow-md shadow-primary/30 ring-1 ring-white/10"
            : "text-muted-foreground hover:bg-accent hover:text-foreground"
        }`}
      >
        <Icon width="1.3em" height="1.3em" className="shrink-0" strokeWidth={2} />
        {!collapsed && (
          <>
            <span class="flex-1 truncate">{section.label}</span>
            {hasChildren && (
              <ChevronDown
                width="1em"
                height="1em"
                class={`shrink-0 transition-transform ${expanded ? "" : "-rotate-90"}`}
              />
            )}
          </>
        )}
      </button>
      {hasChildren && expanded && !collapsed && (
        <div class="ml-3 mt-1 flex flex-col gap-0.5 border-l border-border pl-3">
          {section.children!.map((child) => {
            const ChildIcon = child.icon;
            const isChildActive = activeKey === child.key;
            return (
              <button
                key={child.key}
                type="button"
                onClick={() => onChildClick?.(child.key)}
                class={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors ${
                  isChildActive
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {ChildIcon && <ChildIcon width="1em" height="1em" className="shrink-0" />}
                <span class="flex-1 text-left truncate">{child.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}
